import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import SectionHeading from '../../../components/common/SectionHeading';

const Section = styled.section`
  margin-top: 4rem;
  padding: 2.5rem;
  border-radius: 1.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.surface} 0%, ${({ theme }) => theme.colors.surfaceMuted} 100%);
  box-shadow: ${({ theme }) => theme.shadows.md};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 0;
  }

  @media (max-width: 768px) {
    padding: 1.75rem;
  }
`;

const CarouselWrapper = styled.div`
  margin-top: 2rem;
  position: relative;
  z-index: 1;
`;

const Viewport = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 1.5rem;
`;

const SlidesContainer = styled.div`
  display: flex;
  transition: transform 600ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(${({ activeIndex, cardsPerView }) => `-${activeIndex * (100 / cardsPerView)}%`});
  gap: 1.5rem;

  @media (max-width: 1024px) {
    gap: 1rem;
  }

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const QuoteCard = styled.div`
  flex: 0 0 ${({ cardsPerView }) => `calc((100% - ${(cardsPerView - 1) * 1.5}rem) / ${cardsPerView})`};
  padding: 2.5rem;
  border-radius: 1.5rem;
  background: ${({ theme }) => theme.colors.surface};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  border: 1px solid rgba(102, 126, 234, 0.1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  min-height: 280px;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: rgba(102, 126, 234, 0.3);

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 1024px) {
    flex: 0 0 ${({ cardsPerView }) => `calc((100% - ${(cardsPerView - 1) * 1}rem) / ${cardsPerView})`};
    padding: 2rem;
    min-height: 260px;
  }

  @media (max-width: 768px) {
    flex: 0 0 ${({ cardsPerView }) => `calc((100% - ${(cardsPerView - 1) * 0.75}rem) / ${cardsPerView})`};
    padding: 1.75rem;
    min-height: 240px;
  }
`;

const QuoteIcon = styled.div`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  opacity: 0.15;
  color: ${({ theme }) => theme.colors.primary};
  transition: all 0.3s ease;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border-radius: 50%;
  position: relative;

  &::before {
    content: '❝';
    position: absolute;
    font-size: 2.5rem;
    color: ${({ theme }) => theme.colors.primary};
  }

  ${QuoteCard}:hover & {
    transform: scale(1.1);
    opacity: 0.25;
  }
`;

const QuoteText = styled.p`
  font-size: 1.125rem;
  line-height: 1.9;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.body};
  position: relative;
  z-index: 1;
  font-weight: 400;
  flex: 1;
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const ControlButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.md};
  cursor: pointer;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: ${({ theme }) => theme.transitions.default};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    background: ${({ theme }) => theme.colors.primary};
    color: white;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Dots = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${({ active, theme }) => (active ? theme.colors.primary : theme.colors.surfaceMuted)};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  padding: 0;

  &:hover {
    background: ${({ theme, active }) => (active ? theme.colors.primary : theme.colors.textMuted)};
  }
`;

const quotes = [
  {
    text: '오늘 코딩 2시간한다고 신나서 갔는데 내일도 2시간이라 좋아합니다 ~^^',
  },
  {
    text: '학원중에 제일 좋아하는 학원이라고 하더라고요~ 항상 즐겁게 가르쳐 주셔서 감사합니다~',
  },
  {
    text: '이번 코딩 활동을 통해 자신감도 많이 얻고, 트로피와 상장도 받아서 무척 기뻐했습니다. 피자도 먹었다며 하루 종일 즐거워하더라고요.',
  },
  {
    text: '선생님이 잘 지도해 주신 덕분에 나날이 성장 하는 모습에 너무 감사해요~',
  },
  {
    text: '코딩에 흥미를 많이 느끼고 즐겁게 수업 하는 것 같아요~',
  },
];

const ParentQuoteSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(3);

  useEffect(() => {
    const updateCardsPerView = () => {
      if (window.innerWidth <= 768) {
        setCardsPerView(1);
      } else if (window.innerWidth <= 1024) {
        setCardsPerView(2);
      } else {
        setCardsPerView(3);
      }
    };

    updateCardsPerView();
    window.addEventListener('resize', updateCardsPerView);
    return () => window.removeEventListener('resize', updateCardsPerView);
  }, []);

  const maxIndex = Math.max(0, quotes.length - cardsPerView);

  const goTo = (index) => {
    const newIndex = Math.max(0, Math.min(index, maxIndex));
    setActiveIndex(newIndex);
  };

  const goNext = () => {
    goTo(activeIndex + 1);
  };

  const goPrev = () => {
    goTo(activeIndex - 1);
  };

  useEffect(() => {
    if (activeIndex > maxIndex) {
      setActiveIndex(Math.max(0, maxIndex));
    }
  }, [cardsPerView, maxIndex, activeIndex]);

  return (
    <Section id="parent-quotes">
      <SectionHeading
        eyebrow="학부모님의 소리"
        title="학부모님 후기"
        description="우리 학원을 경험하신 학부모님들의 소중한 이야기입니다."
      />
      <CarouselWrapper>
        <Viewport>
          <SlidesContainer activeIndex={activeIndex} cardsPerView={cardsPerView}>
            {quotes.map((quote, index) => (
              <QuoteCard key={index} cardsPerView={cardsPerView}>
                <QuoteIcon />
                <QuoteText>{quote.text}</QuoteText>
              </QuoteCard>
            ))}
          </SlidesContainer>
        </Viewport>
        <Controls>
          <ControlButton
            type="button"
            aria-label="이전 후기"
            onClick={goPrev}
            disabled={activeIndex === 0}
          >
            ‹
          </ControlButton>
          <Dots>
            {Array.from({ length: maxIndex + 1 }).map((_, index) => (
              <Dot
                key={index}
                type="button"
                aria-label={`후기 ${index + 1}로 이동`}
                active={index === activeIndex}
                onClick={() => goTo(index)}
              />
            ))}
          </Dots>
          <ControlButton
            type="button"
            aria-label="다음 후기"
            onClick={goNext}
            disabled={activeIndex >= maxIndex}
          >
            ›
          </ControlButton>
        </Controls>
      </CarouselWrapper>
    </Section>
  );
};

export default ParentQuoteSection;

